// The QUOTE slice's pure kernel (Module 09 §2.12–§2.15, §3.3–§3.5, §4.4–§4.6,
// slice P3-C-1) — chip vocabulary, validity arithmetic, the ATP traffic-light
// band, and the line/total money math the composer previews with.
//
// EVERYTHING HERE IS PURE AND UNIT-TESTED. No React, no wire types: the
// composer, the quote list, the review inbox and the public accept page all
// read the same functions, so a status cannot wear one colour on the list and
// another on the document, and a line total cannot be computed two ways.
//
// THE MONEY RULE (repo-wide): integer minor units, BigInt intermediates, one
// divide at the end with BANKER'S ROUNDING (§2.14: "round(qty × unit_price)
// banker's rounding at line level"). `qty` is `numeric(14,3)`, so it enters as
// milli-units parsed by exact string math (lib/price-import.decimalToMinor),
// never `parseFloat`.
//
// AND THE PREVIEW RULE: these totals are a PREVIEW of what the server will
// write, never the value that is stored. `POST /v1/quotes` and
// `POST /v1/quotes/{id}/revisions` return server-computed line totals and
// revision subtotal/tax/total, and the composer renders THOSE once they land
// (a draft that has been saved shows the server's numbers). The preview exists
// so a manager typing a line sees a total before the round-trip — that is the
// whole of its job, and the header on `previewTotals` says so where a future
// reader will meet it.

import type { ChipVariant } from "../../../../components/status-chip";
import { decimalToMinor } from "./price-import";
import { normalizeTypedDecimal } from "./money";

/** `quotes.status` (§2.12 CHECK) — the client mirror of the wire enum. The
 * generated union replaces this the moment the contract lands; until then the
 * screens compare against these literals and the mapping below is total. */
export const QUOTE_STATUSES = [
  "draft",
  "pending_approval",
  "approved",
  "sent",
  "accepted",
  "converted",
  "rejected",
  "expired",
  "cancelled",
] as const;

export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export function isQuoteStatus(value: unknown): value is QuoteStatus {
  return typeof value === "string" && (QUOTE_STATUSES as readonly string[]).includes(value);
}

/**
 * §3.4's state machine → the register's five chip variants. The product-wide
 * law (status-chip.tsx) is green = good/confirmed, amber = attention, red =
 * failed — so the mapping is about WHAT THE READER MUST DO, not about how far
 * along the document is:
 *
 *   draft, cancelled          neutral — a fact, no verdict, nothing to do.
 *   pending_approval          WARN    — somebody is waiting on a decision.
 *   expired                   WARN    — attention, not failure: §3.4 makes
 *                                       `expired` re-openable via a revision,
 *                                       so red would claim a terminal state
 *                                       the machine does not have.
 *   approved, sent            ACCENT  — in flight, the governed states (the
 *                                       tier chip uses accent for the same
 *                                       reason: this is the row the process
 *                                       is currently about).
 *   accepted, converted       OK      — the customer said yes.
 *   rejected                  BAD     — the one genuinely lost outcome.
 */
export function quoteStatusVariant(status: string): ChipVariant {
  switch (status) {
    case "pending_approval":
    case "expired":
      return "warn";
    case "approved":
    case "sent":
      return "accent";
    case "accepted":
    case "converted":
      return "ok";
    case "rejected":
      return "bad";
    default:
      return "neutral";
  }
}

/**
 * THE TARGET-CUSTOMER RULE, AS THE SERVER ANSWERED IT (task #229) — would
 * §4.4's two document-minting doors open for this reader?
 *
 * `POST /v1/quotes/{id}/revisions` ("Save as a new version") and
 * `POST /v1/quotes/{id}/duplicate` both call `requireCustomer` as their FIRST
 * act, before the status arm either of them owns, so ONE flag answers for
 * both: false on an archived customer (422 `CUSTOMER_ARCHIVED`, §3.1.4) and on
 * a quote a sales rep still OWNS after being unassigned from its customer (404
 * `CUSTOMER_NOT_FOUND` — the quote read admits that reader, this rule does
 * not). It says nothing about `sales.quote.write`, which comes from the role
 * mirror, and nothing about the quote's status, which `isTerminal` answers.
 *
 * THE KEY IS OPTIONAL HERE, exactly as `orderRequotable`'s `requotable` is
 * (D-289): a payload from an API build that predates it — the deploy-roll
 * window, D-230 — reads as quotable, which is the behaviour these screens had
 * before the flag existed, with the server's refusal still standing. A payload
 * that is simply missing the key must not read as "blocked" and paint a
 * sentence about an archive that nobody performed.
 *
 * It lives in the kernel rather than in the screen because the predicate is
 * pure and the screen is not; the order's twin sits in `order-detail.tsx` for
 * the historical reason that it was written there first.
 *
 * `id` IS IN THE PARAMETER TYPE TO DEFEAT TYPESCRIPT'S WEAK-TYPE CHECK, not
 * because the body reads it. A parameter whose every property is optional is a
 * "weak type", and passing a `QuoteDetail` minted from an api-client that does
 * not yet declare `customer_quotable` — the state of the generated client
 * between a contract landing and its regeneration — shares no property with it
 * and fails TS2559. One required property a quote always has turns that into
 * the ordinary structural check it should be, at both ends of the
 * regeneration.
 */
export function quoteCustomerQuotable(quote: { id: string; customer_quotable?: boolean }): boolean {
  return quote.customer_quotable !== false;
}

/**
 * WHICH ARM closed those doors, so the sentence beside the missing control can
 * name a remedy rather than a fact (task #229). `customer_status` is the
 * customer as §3.1.4 left them; when it is `archived` the remedy is to restore
 * the customer, and when it is not, a false `customer_quotable` can only be
 * the assignment arm, whose remedy is an administrator.
 *
 * A caller archived AND unassigned is named as archived — the first true
 * sentence, not the first refusal the server would raise (which is the 404).
 * Both blocks are real, and restoring the customer is the one the reader can
 * ask for by name; the assignment arm surfaces on the next read.
 *
 * Optional for the same deploy-roll reason as above: an older payload carries
 * no status, and "not archived" is then the honest reading, since the flag it
 * would explain is absent too.
 */
export function quoteCustomerArchived(quote: { id: string; customer_status?: string }): boolean {
  return quote.customer_status === "archived";
}

/** §2.14 `price_source` — the composer's source chip (§4.4: `tier`,
 * `override`, `off-cut −30%`, `manual` in red when off-list). */
export const PRICE_SOURCES = ["tier_list", "customer_override", "offcut_class", "manual"] as const;
export type PriceSource = (typeof PRICE_SOURCES)[number];

/**
 * The source chip's variant. `manual` is the only RED one, and §4.4 says why:
 * a manually typed price is the off-list act tier discipline (§2.21/§10 S13)
 * exists to count, so it is loud at the moment it is typed rather than
 * discovered in a report a month later. `offcut_class` is ACCENT — it is the
 * governed discount, the thing the off-cut programme wants noticed.
 */
export function priceSourceVariant(source: PriceSource): ChipVariant {
  // THE MAPPING IS TOTAL, and the parameter is the ENUM rather than `string` so
  // the compiler says so. A `string` with a `default` arm ships a fifth
  // `PriceSource` member as a silent neutral chip — a new kind of price wearing
  // the colour of an ordinary one, on the screen that decides what a customer
  // pays. `quoteStatusVariant` above claims totality in a comment; this one now
  // has the compiler behind it. (`satisfies never` on the exhaustive switch: add
  // a member to the enum and this stops building.)
  switch (source) {
    case "manual":
      return "bad";
    case "offcut_class":
      return "accent";
    case "customer_override":
      return "warn";
    case "tier_list":
      return "neutral";
  }
}

// ---------------------------------------------------------------------------
// Validity (§4.5: "valid_until, red when < 3 days")
// ---------------------------------------------------------------------------

/** §4.5's threshold. Stated once on this side; the customer's page states its
 * own copy (`areas/public/lib/public-view.URGENT_DAYS`) because the public
 * bundle carries no office code, and the two must agree — they disagreed until
 * 2026-09-10, when the page's band was `<= 3` and this one `< 3`. */
export const VALIDITY_URGENT_DAYS = 3;

export type ValidityBand = "absent" | "expired" | "urgent" | "ok";

/**
 * Whole days from `fromIso` to `toIso`, both `YYYY-MM-DD` BUSINESS dates.
 *
 * Business dates, so this is calendar arithmetic and NOT a timezone question:
 * both ends are parsed at UTC midnight, which is exactly the comparison
 * "is today past the valid_until printed on the offer" — the same comparison
 * §3.4's expiry cron makes in the tenant's HQ zone. Returns `undefined` for
 * anything that is not a business date rather than a misleading zero.
 */
export function daysBetweenIso(fromIso: string, toIso: string): number | undefined {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return undefined;
  return Math.round((to - from) / 86_400_000);
}

/**
 * The §4.5 validity band of a quote, given today's business date.
 *
 * `absent` is a DRAFT's honest state: `valid_until` is NULL until the quote is
 * sent (§2.12 — "set on send"), so there is no countdown to render and the
 * cell renders nothing rather than a dash or a zero.
 */
export function validityBand(
  validUntil: string | null | undefined,
  todayIso: string,
): ValidityBand {
  if (validUntil === null || validUntil === undefined || validUntil.length === 0) return "absent";
  const days = daysBetweenIso(todayIso, validUntil);
  if (days === undefined) return "absent";
  if (days < 0) return "expired";
  if (days < VALIDITY_URGENT_DAYS) return "urgent";
  return "ok";
}

/** Days remaining on the offer (0 on the last day), or `undefined` when there
 * is no `valid_until` yet. Negative once past — the public page's expired
 * state uses the sign, never the magnitude. */
export function daysRemaining(
  validUntil: string | null | undefined,
  todayIso: string,
): number | undefined {
  if (validUntil === null || validUntil === undefined || validUntil.length === 0) return undefined;
  return daysBetweenIso(todayIso, validUntil);
}

// ---------------------------------------------------------------------------
// The ATP traffic-light (D-241 DEEPEN-EXISTING — presentation only)
// ---------------------------------------------------------------------------

/**
 * The four lights of `AtpLight` (the wire enum). FOUR, not three, because "we
 * could not ask" is not amber — a light that renders green on a failed ATP
 * read is worse than no light at all.
 *
 * THE BAND IS NOT COMPUTED HERE. Module 06 owns availability and the
 * fulfilment-options endpoint returns the verdict already decided, so the
 * client's whole job is colour and words. A client-side re-derivation would be
 * a second source of truth for the one fact the composer must not get wrong.
 */
export type AtpBand = "green" | "amber" | "red" | "unknown";

export function isAtpBand(value: unknown): value is AtpBand {
  return value === "green" || value === "amber" || value === "red" || value === "unknown";
}

/** The chip variant for a band — `unknown` is NEUTRAL, so a silent ATP read
 * reads as "no answer" rather than as any verdict. */
export function atpChipVariant(band: AtpBand): ChipVariant {
  switch (band) {
    case "green":
      return "ok";
    case "amber":
      return "warn";
    case "red":
      return "bad";
    default:
      return "neutral";
  }
}

// ---------------------------------------------------------------------------
// Line + document money (§2.13/§2.14 — PREVIEW ONLY, see the file header)
// ---------------------------------------------------------------------------

/** Quantity text the composer accepts: `numeric(14,3)`, strictly positive
 * (§2.14 CHECK > 0). Shared with the composer's input validation so the field
 * and the math agree on what a quantity is. */
export const QTY_TEXT = /^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/u;

/** Quantity text → milli-units by exact string math, or `undefined`. */
export function qtyToMilli(text: string): number | undefined {
  if (!QTY_TEXT.test(text.trim())) return undefined;
  return decimalToMinor(text.trim(), 3);
}

/**
 * A TYPED quantity, read in the active locale first (D-274).
 *
 * The price box one column to the right learned the factory's keyboard and this
 * one had not, so a Russian manager typing `2,5` metres got the price accepted
 * and the quantity refused — the same complaint, one control apart, which is
 * worse than refusing both. `normalizeTypedDecimal` only ever yields the
 * canonical `D+(.D+)?` or returns its input untouched, so `QTY_TEXT`'s own
 * grammar — at least one non-zero digit, ≤ 11 integer digits, ≤ 3 decimals —
 * is applied to exactly what it was applied to before, and nothing that was
 * refused becomes a DIFFERENT number.
 */
export function qtyTypedOk(raw: string, locale: string): boolean {
  return QTY_TEXT.test(normalizeTypedDecimal(raw.trim(), locale));
}

/** The typed quantity's value in milli-units, or `undefined` if it is not one. */
export function qtyTypedToMilli(raw: string, locale: string): number | undefined {
  return qtyToMilli(normalizeTypedDecimal(raw.trim(), locale));
}

/** Round-half-to-EVEN division of BigInts (banker's rounding, §2.14). Both
 * arguments non-negative; `denominator > 0`. */
export function divRoundHalfEven(numerator: bigint, denominator: bigint): bigint {
  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  const twice = remainder * 2n;
  if (twice > denominator) return quotient + 1n;
  if (twice < denominator) return quotient;
  // Exactly half: to even.
  return quotient % 2n === 0n ? quotient : quotient + 1n;
}

/**
 * §2.14's `line_total_minor` = `round(qty × unit_price_minor)`, banker's, from
 * the quantity TEXT (never a float). Returns `undefined` when the quantity is
 * not a valid `numeric(14,3)` — the caller renders the typed text back, which
 * is what the price grid does with an unparseable price.
 */
export function lineTotalMinor(qtyText: string, unitPriceMinor: number): number | undefined {
  const milli = qtyToMilli(qtyText);
  if (milli === undefined) return undefined;
  if (!Number.isSafeInteger(unitPriceMinor) || unitPriceMinor < 0) return undefined;
  const total = divRoundHalfEven(BigInt(milli) * BigInt(unitPriceMinor), 1000n);
  if (total > BigInt(Number.MAX_SAFE_INTEGER)) return undefined;
  return Number(total);
}

/** §2.7's `vat_mode` (D-234) — whether the list's prices already contain VAT. */
export type VatMode = "inclusive" | "exclusive";

export interface DocumentTotals {
  readonly subtotalMinor: number;
  readonly taxMinor: number;
  readonly totalMinor: number;
}

/**
 * The composer's totals PREVIEW (§2.13 + §3.2's D-234 rule).
 *
 * `exclusive` — the printed prices are net: tax rides on top and the customer
 *   pays subtotal + tax.
 * `inclusive` — the printed prices already contain the tax (KZ market lists,
 *   D-234's seeded default): the total IS the subtotal, and the tax line is
 *   the portion of it that is VAT — `total − round(total × 100 / (100 + rate))`
 *   — which is what a Kazakh счёт prints as «в том числе НДС».
 *
 * `ratePct` is the D-022 tenant seed rate (KZ 16.00 in 2026) until module 11's
 * tax config lands (P4) and takes over; the caller passes it, this function
 * never assumes it.
 */
export function previewTotals(
  lineTotals: readonly number[],
  vatMode: VatMode,
  ratePct: number,
): DocumentTotals {
  let subtotal = 0n;
  for (const total of lineTotals) subtotal += BigInt(Math.trunc(total));
  // Rate as an integer basis-point-ish scale so 16.00 does not enter as a
  // float: two decimals is what `tax_rate` carries.
  const rateHundredths = BigInt(Math.round(ratePct * 100));
  if (rateHundredths <= 0n) {
    const flat = Number(subtotal);
    return { subtotalMinor: flat, taxMinor: 0, totalMinor: flat };
  }
  if (vatMode === "exclusive") {
    const tax = divRoundHalfEven(subtotal * rateHundredths, 10_000n);
    return {
      subtotalMinor: Number(subtotal),
      taxMinor: Number(tax),
      totalMinor: Number(subtotal + tax),
    };
  }
  const net = divRoundHalfEven(subtotal * 10_000n, 10_000n + rateHundredths);
  return {
    subtotalMinor: Number(net),
    taxMinor: Number(subtotal - net),
    totalMinor: Number(subtotal),
  };
}

// ---------------------------------------------------------------------------
// §7 field-level redaction — OMISSION, restated for quote lines
// ---------------------------------------------------------------------------

/**
 * The margin keys §7 strips for a role without `sales.margin.read`.
 *
 * NULL COUNTS AS ABSENT here, and the wire is why: the generated types render
 * these as `?: number | null` — optional because §7 omits them per role, and
 * nullable because a line can legitimately have no guard at all (§2.14's
 * `margin_guard_pct` is NULL when none applied). The screens must not care
 * which of the two produced the emptiness, because the rendering is the same
 * in both cases: nothing.
 */
export interface MarginBearingLine {
  readonly unit_cost_minor?: number | null | undefined;
  readonly margin_pct?: string | number | null | undefined;
  readonly margin_guard_pct?: string | number | null | undefined;
  readonly margin_breached?: boolean | undefined;
}

/**
 * Does this payload carry the margin block at all?
 *
 * §7 is literal — "redacted keys are OMITTED from the payload, never null,
 * never a marker" — so the composer's margin block renders IFF the keys are
 * present. A role without the grant gets NO BLOCK: not a dash, not a lock
 * icon, not a masked figure. What it does get is the always-present
 * `margin_breached` boolean rendered as §4.4's neutral "needs approval" flag,
 * which is the only margin fact that role is entitled to.
 */
export function marginVisible(line: MarginBearingLine): boolean {
  return (
    (line.unit_cost_minor !== undefined && line.unit_cost_minor !== null) ||
    (line.margin_pct !== undefined && line.margin_pct !== null)
  );
}

// ---------------------------------------------------------------------------
// §2.12's one reason column, read back
// ---------------------------------------------------------------------------

/** The four §5 rejection codes → the i18n key each is READ BACK as. A code is
 * a wire token; printing `lead_time` at a Russian manager is the same defect
 * as a server minting English prose into a displayed column. Indexed by a bare
 * string on purpose — the lookup's whole job is answering "is this prefix one
 * of the four", and a `Record<Code, string>` would type the miss away. */
const REJECTION_CODE_KEYS: Readonly<Record<string, string | undefined>> = {
  price: "sales.composer.rejectReasonPrice",
  lead_time: "sales.composer.rejectReasonLeadTime",
  stock: "sales.composer.rejectReasonStock",
  other: "sales.composer.rejectReasonOther",
};

/**
 * `quotes.rejected_reason` holds what the TWO closing doors recorded, and they
 * write it differently — so a screen reading it back has to know which door it
 * came from, which is exactly what `status` says.
 *
 *   rejected  → `<code>` or `<code>: <the customer's words>` (quotes.service:
 *               "the CODE plus the words — one column, and the prefix is what
 *               makes P3-E's loss analysis groupable").
 *   cancelled → the seller's own words, VERBATIM. `markCancelled` deliberately
 *               stopped prefixing them (D-269): a server that edits what a
 *               human typed is the defect, so nothing here may un-edit them.
 *
 * Splitting only on `rejected` is what keeps a cancellation that happens to
 * begin "price: agreed with the buyer" from being torn in half and relabelled.
 * An unrecognised prefix on a rejection is left whole for the same reason —
 * this is a display helper, not a parser with a right to be wrong.
 */
export function rejectedReasonParts(
  status: string,
  reason: string,
): { readonly codeKey: string | undefined; readonly note: string | undefined } {
  const whole = reason.trim();
  if (whole.length === 0) return { codeKey: undefined, note: undefined };
  if (status !== "rejected") return { codeKey: undefined, note: whole };
  const cut = whole.indexOf(":");
  const head = (cut === -1 ? whole : whole.slice(0, cut)).trim();
  const codeKey = REJECTION_CODE_KEYS[head];
  if (codeKey === undefined) return { codeKey: undefined, note: whole };
  const rest = cut === -1 ? "" : whole.slice(cut + 1).trim();
  return { codeKey, note: rest.length > 0 ? rest : undefined };
}

// ---------------------------------------------------------------------------
// §4.6 review inbox — the five card kinds
// ---------------------------------------------------------------------------

/**
 * §4.6's card kinds under their WIRE names, and the crucial asymmetry between
 * them: four carry a DECISION (an Approve/Reject pair on a §5 decide endpoint)
 * and one does not.
 *
 *   quote_margin_breach     → POST /v1/quotes/{id}/decide      (sales.margin.approve)
 *   order_margin_breach     → POST /v1/orders/{id}/decide      (sales.margin.approve)
 *   price_override          → POST /v1/customer-price-overrides/{id}/decide
 *                                                              (sales.margin.approve)
 *   credit_block            → POST /v1/orders/{id}/credit-decision
 *                                                              (sales.credit.approve)
 *   fulfillment_degradation → NOTHING. §4.6: "Fulfilment-degradation cards
 *                       render informational with no decide action: the order
 *                       already exists, and the card is the visibility." An
 *                       Approve button on it would be a control with no
 *                       endpoint.
 *
 * The names above are the ones on the wire, not prose shorthands: this block
 * used to say "four card kinds … three carry a decision" and list five
 * invented names (`quote_margin`, `override_margin`, `order_credit`,
 * `order_margin`, `fulfilment`), none of which a `switch` below or a
 * `ReviewCard.kind` on the wire ever matches. A reader checking whether a kind
 * is handled has to be able to grep the comment against the code.
 */
export const REVIEW_KINDS = [
  "quote_margin_breach",
  "order_margin_breach",
  "price_override",
  "credit_block",
  "fulfillment_degradation",
] as const;

export type ReviewKind = (typeof REVIEW_KINDS)[number];

/**
 * Which permission code decides this card — `undefined` for the one kind that
 * is not decided at all.
 *
 * This is NOT the gate. `ReviewCard.decidable_by_caller` is, because only the
 * server can evaluate §3.4's threshold (breach ≤2pp → facility_admin; >2pp or
 * any breached line over the cap → group_admin only), and a client that
 * guessed it would either offer a button that 403s or hide one a group_admin
 * genuinely holds. This function answers the narrower question the CARD needs:
 * which grant is even in the conversation, so a reader without it is told that
 * rather than shown a dead control.
 */
export function reviewDecisionPermission(kind: ReviewKind): string | undefined {
  switch (kind) {
    case "quote_margin_breach":
    case "order_margin_breach":
    case "price_override":
      return "sales.margin.approve";
    case "credit_block":
      return "sales.credit.approve";
    default:
      return undefined;
  }
}

/** §4.6: the degradation card has NO decide action — the order already exists
 * and the card is the visibility. A property of the kind, so no screen has to
 * remember it. */
export function reviewIsDecidable(kind: ReviewKind): boolean {
  return reviewDecisionPermission(kind) !== undefined;
}

/** The §4.6 card's variant: a credit block is the loudest (fulfilment is
 * already blocked behind it), a margin breach is attention, a degradation is
 * a fact that already happened. */
export function reviewKindVariant(kind: ReviewKind): ChipVariant {
  switch (kind) {
    case "credit_block":
      return "bad";
    case "fulfillment_degradation":
      return "neutral";
    default:
      return "warn";
  }
}

/** Whole days a card has been waiting, for §4.6's age column. */
export function ageInDays(requestedAtIso: string, nowMs: number): number | undefined {
  const at = Date.parse(requestedAtIso);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.floor((nowMs - at) / 86_400_000));
}

// ---------------------------------------------------------------------------
// The public accept page's five states (§3.5.1 / §4.10)
// ---------------------------------------------------------------------------

/**
 * §4.10's five DESIGNED pages, one per resolution of the token. They are
 * states of a public document, not error codes: `not_found` is the only one
 * that renders as a 404, and it renders GENERICALLY — no tenant name, no
 * branding, nothing that would confirm a guessed token belongs to anybody.
 */
export const PUBLIC_QUOTE_STATES = [
  "valid",
  "expired",
  "superseded",
  "accepted",
  "not_found",
] as const;

export type PublicQuoteState = (typeof PUBLIC_QUOTE_STATES)[number];

export function isPublicQuoteState(value: unknown): value is PublicQuoteState {
  return typeof value === "string" && (PUBLIC_QUOTE_STATES as readonly string[]).includes(value);
}

/**
 * `declined` is deliberately NOT one of them: declining is an ACT the visitor
 * performs on this page, not a state the token resolves to. The server records
 * it and the page swaps to its own thank-you view in the same paint — a
 * refresh afterwards resolves the token again and lands wherever the document
 * now is. Modelling it as a sixth wire state would mean a customer who
 * declined and re-opened the link got a page that could only say "you declined
 * this", which is worse than the truth.
 */
export type PublicQuoteView = PublicQuoteState | "declined";
